// Post an approved payroll run to the tenant's accounting system.
//
// This lives where the accountant already is — on the run — rather than on the
// Accounting settings screen, because posting is something you do to a run,
// not to a connection. Fern's answer to Q11 is that accountants press Post and
// nothing posts automatically, so this button is the ONLY way a payroll
// journal reaches their books.
//
// Everything it refuses to do is deliberate:
//   · it does not appear at all unless the ledger is connected, so an
//     unconfigured tenant is never shown a button that cannot work;
//   · it never says "posted" from an optimistic update — the badge only
//     changes once the server has said so and named the document;
//   · a second press while one is in flight is impossible, and a second press
//     after success is answered by the server with the original document
//     rather than a duplicate journal.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, BookUp, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { usePermissions } from '@/features/auth/route-protection/hooks/usePermissions';
import { getApiErrorMessage } from '@/lib/apiError';
import { UnknownPostingResolver } from './UnknownPostingResolver';
import {
  firstOfNextMonth, getAccountingStatus, getPostings, postPayrollRun,
  isOutcomeUnknown, postPayrollSettlement, previewPayrollSettlement, redatePosting,
  type AccountingProvider, type AccountingStatus, type PayrollDraft, type Posting,
  type PostingIssue,
} from '@/services/accountingService';

interface Props {
  runId: string;
  /** The run's state. Only an approved (or later) run can be posted. */
  runState: string;
  /** The company the run belongs to. A run has exactly one — it is NOT NULL on
   *  the run and part of its period key — and it decides which ledger this
   *  posts to. Taken from the run rather than the workspace, because a group's
   *  runs each go to their own company's books. */
  legalEntityId: string;
  provider?: AccountingProvider;
}

const POSTABLE_STATES = new Set(['approved', 'locked', 'paid']);

export const AccountingPostCard: React.FC<Props> = ({ runId, runState, legalEntityId, provider = 'quickbooks' }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [status, setStatus] = useState<AccountingStatus | null>(null);
  const [posting, setPosting] = useState<Posting | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const canPost = hasPermission('accounting:post');
  const systemName = status?.label || t('accounting.defaultSystem', { defaultValue: 'the accounting system' });

  // The day to move a refused journal to. A suggestion in an editable field —
  // nothing here knows which periods the ledger has open, and the person doing
  // the moving does.
  const [newDate, setNewDate] = useState('');

  const load = useCallback(async () => {
    try {
      // The run's own company, like the postings below it: read tenant-wide,
      // a Fern run would have read as connected on the strength of another
      // company's grant (O-10).
      const st = await getAccountingStatus(provider, legalEntityId);
      setStatus(st);
      if (st.connected) {
        // The run's own company. Postings are per company now, so asking with
        // the tenant would show another company's history under this run.
        const rows = await getPostings(provider, legalEntityId, 100);
        setPosting(rows.find((p) => p.source_id === runId) ?? null);
      }
    } catch {
      // A settings screen being unreachable must not break the run page it is
      // embedded in. The card simply does not render.
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [provider, runId, legalEntityId]);

  useEffect(() => { void load(); }, [load]);

  // Record the new date, then post through the normal path — so what reaches
  // the ledger is still what a preview would show, and the key rotation that
  // makes a re-dated document a NEW document happens server-side.
  const moveAndPost = async () => {
    const when = newDate || firstOfNextMonth(posting?.doc_date);
    setBusy(true);
    try {
      await redatePosting(provider, legalEntityId, 'payroll_accrual', runId, when);
    } catch (e) {
      // The date was refused, or there was nothing waiting. Say so and stop —
      // posting anyway would just hit the closed period again.
      toast.error(getApiErrorMessage(e, t('accounting.redateFailed', {
        defaultValue: 'Could not move this journal.',
      })));
      setBusy(false);
      return;
    }
    setBusy(false);
    await post();
  };

  const post = async () => {
    setBusy(true);
    try {
      const out = await postPayrollRun(provider, runId);
      setPosting(out.posting);
      if (out.already_posted) {
        // Not an error, and not a silent no-op either: say plainly that
        // nothing was sent, so nobody goes looking for a second journal.
        toast(t('accounting.alreadyPosted', {
          defaultValue: 'Already posted to {{system}} as {{doc}} — nothing was sent again.',
          system: systemName,
          doc: out.posting.external_doc_number || out.posting.external_doc_id,
        }), { duration: 7000 });
      } else {
        toast.success(t('accounting.postedOk', {
          defaultValue: 'Posted to {{system}} as {{doc}}.',
          system: systemName,
          doc: out.posting.external_doc_number || out.posting.external_doc_id,
        }), { duration: 7000 });
      }
    } catch (e) {
      if (isOutcomeUnknown(e)) {
        // The send's answer was lost: reload so the card shows the unknown
        // state and its two answers, rather than a retry.
        toast.error(getApiErrorMessage(e, ''), { duration: 12000 });
        void load();
        return;
      }
      // Say what the server said. Every refusal here is actionable — an
      // unmapped account names the accounts, a closed period names the month —
      // and replacing that with "could not post" throws the answer away.
      toast.error(getApiErrorMessage(e, t('accounting.postFailed', {
        defaultValue: 'Could not post to {{system}}.', system: systemName,
      })), { duration: 9000 });
      void load();
    } finally {
      setBusy(false);
    }
  };

  // Nothing to offer: no connection, still loading, or a run whose numbers are
  // not final. Showing a disabled button in those cases invites the question
  // "why is this greyed out?" for which there is no answer on this screen.
  if (loading || !status?.connected) return null;
  if (!POSTABLE_STATES.has(String(runState).toLowerCase()) && !posting) return null;

  const posted = posting?.status === 'posted';
  const needsRedate = posting?.status === 'needs_redate';
  const unknown = posting?.status === 'unknown';

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <BookUp className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">
                {t('accounting.postTitle', { defaultValue: '{{system}}', system: systemName })}
              </span>
              {posted && (
                <Badge variant="outline" className="border-success/40 text-success">
                  <CheckCircle2 className="mr-1 h-3 w-3" aria-hidden />
                  {t('accounting.statusPosted', { defaultValue: 'Posted' })}
                </Badge>
              )}
              {unknown && (
                <Badge variant="outline" className="border-warning/40 text-warning">
                  <AlertTriangle className="mr-1 h-3 w-3" aria-hidden />
                  {t('accounting.statusUnknown', { defaultValue: 'Outcome unknown' })}
                </Badge>
              )}
              {needsRedate && (
                <Badge variant="outline" className="border-warning/40 text-warning">
                  <AlertTriangle className="mr-1 h-3 w-3" aria-hidden />
                  {t('accounting.statusClosedPeriod', { defaultValue: 'Period closed' })}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {posted
                ? t('accounting.postedDetail', {
                    defaultValue: 'Journal {{doc}}, dated {{date}}.',
                    doc: posting?.external_doc_number || posting?.external_doc_id,
                    date: posting?.doc_date ?? '—',
                  })
                : needsRedate
                  ? t('accounting.closedPeriodDetail', {
                      defaultValue: 'That month is closed in {{system}}, so nothing was posted. Choose a day in an open month to move it to.',
                      system: systemName,
                    })
                  : t('accounting.notPostedYet', {
                      defaultValue: 'This run has not been posted. The journal is dated the first of the payroll month.',
                    })}
              {posting?.status === 'failed' && posting.last_error && (
                <> {' · '}<span className="text-danger">{posting.last_error}</span></>
              )}
            </p>
          </div>
        </div>

        {unknown && posting ? (
          // D10: never "Try posting again" — the journal may already be there.
          <UnknownPostingResolver
            provider={provider} legalEntityId={legalEntityId} kind="payroll_accrual" sourceId={runId}
            systemName={systemName} canResolve={canPost} onResolved={setPosting}
          />
        ) : canPost && needsRedate ? (
          // Was "Try posting again", which would fail identically: the date had
          // not changed. The journal is correct and the month is shut, so the
          // only thing that moves it is a different date.
          <div className="flex flex-wrap items-center gap-2" data-testid="redate-controls">
            <label htmlFor="redate" className="text-xs text-muted-foreground">
              {t('accounting.redateTo', { defaultValue: 'Move to' })}
            </label>
            <input
              id="redate"
              type="date"
              className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground"
              value={newDate || firstOfNextMonth(posting?.doc_date)}
              disabled={busy}
              onChange={(e) => setNewDate(e.target.value)}
            />
            <Button size="sm" disabled={busy} onClick={() => void moveAndPost()}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              {t('accounting.redateAndPost', { defaultValue: 'Move and post' })}
            </Button>
          </div>
        ) : canPost ? (
          <Button size="sm" variant={posted ? 'outline' : 'default'} disabled={busy || posted} onClick={() => void post()}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            {posted
              ? t('accounting.postedButton', { defaultValue: 'Posted' })
              : posting?.status === 'failed' || needsRedate
                ? t('accounting.postRetry', { defaultValue: 'Try posting again' })
                : t('accounting.postNow', { defaultValue: 'Post to {{system}}', system: systemName })}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">
            {t('accounting.postNeedsPermission', {
              defaultValue: 'Posting to {{system}} is done by finance.', system: systemName,
            })}
          </span>
        )}
        <PayrollSettlementRow
          runId={runId} provider={provider} canPost={canPost} systemName={systemName}
          legalEntityId={legalEntityId}
        />
      </CardContent>
    </Card>
  );
};

/**
 * The payroll CASH leg, as its own row.
 *
 * The accrual says what the company owes its people; this says it has been
 * paid. NET_PAYABLE was credited by every accrual and debited by nothing, so
 * the net-salary payable only ever grew in the ledger.
 *
 * Renders NOTHING when there is no cash leg to post, which is the common case:
 * the run may not be paid yet, nothing may record the day it was paid, or it
 * may be a migrated run whose settlement happened in the previous system. The
 * server says which; none of those are worth pushing at somebody reading a
 * payroll run.
 */
const PayrollSettlementRow: React.FC<{
  runId: string;
  provider: AccountingProvider;
  canPost: boolean;
  systemName: string;
  legalEntityId: string;
}> = ({ runId, provider, canPost, systemName, legalEntityId }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<PayrollDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState<string | null>(null);
  // D10: the send's answer was lost — resolve, never retry.
  const [unknown, setUnknown] = useState(false);

  const load = useCallback(() => {
    previewPayrollSettlement(provider, runId).then(setDraft).catch(() => setDraft(null));
  }, [provider, runId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    setBusy(true);
    try {
      const out = await postPayrollSettlement(provider, runId);
      setPosted(out.posting?.external_doc_number || out.posting?.external_doc_id || '');
      toast.success(out.already_posted
        ? t('accounting.alreadyPostedShort', { defaultValue: 'Already posted — nothing was sent again.' })
        : t('accounting.cashPosted', { defaultValue: 'Payment posted to {{system}}.', system: systemName }));
      load();
    } catch (e) {
      if (isOutcomeUnknown(e)) setUnknown(true);
      toast.error(getApiErrorMessage(e, t('accounting.cashPostFailed', {
        defaultValue: 'Could not post the payment.',
      })));
    } finally {
      setBusy(false);
    }
  };

  if (!draft) return null;
  const blockers = draft.issues.filter((i: PostingIssue) => i.blocking);

  return (
    <div className="mt-3 space-y-1 rounded-lg border border-border px-3 py-2" data-testid="payroll-settlement">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] text-foreground">
            {t('accounting.cashTitle', { defaultValue: 'Net pay leaving the bank' })}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {t('accounting.cashSub', {
              defaultValue: 'Clears what was owed to employees · {{date}}', date: draft.doc_date,
            })}
          </div>
        </div>
        {canPost && draft.ready && posted === null && !unknown && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void post()}
            data-testid="post-payroll-settlement">
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
            {t('accounting.postCash', { defaultValue: 'Post the payment' })}
          </Button>
        )}
      </div>
      {unknown && (
        <UnknownPostingResolver
          provider={provider} legalEntityId={legalEntityId} kind="payroll_settlement" sourceId={runId}
          systemName={systemName} canResolve={canPost}
          onResolved={(p) => {
            setUnknown(false);
            if (p.status === 'posted') setPosted(p.external_doc_number || p.external_doc_id || '');
          }}
        />
      )}
      {posted !== null && (
        <div className="text-[13px] text-success">
          {posted
            ? t('accounting.postedAsShort', { defaultValue: 'Posted as {{ref}}', ref: posted })
            : t('accounting.postedPlain', { defaultValue: 'Posted' })}
        </div>
      )}
      {blockers.map((i: PostingIssue, n: number) => (
        <div key={`${i.code}-${n}`} className="text-[11px] text-danger">{i.message}</div>
      ))}
    </div>
  );
};

export default AccountingPostCard;
